"use client";
import { useState, useEffect, useCallback, startTransition, memo } from "react";
import { useRouter } from "next/navigation";
import { runIdle } from "@/src/utils/idle-utils";
import PropTypes from "prop-types";
import classNames from "classnames";
import dynamic from "next/dynamic";

import CloseButton from "assets/svg/Icon_Small_Close.svg";

import { getPropertyTree } from "@/src/services/common";
import { urlMaker } from "@/src/services/url-maker";

import { getGa4SearchEventParams } from "src/helpers/google-tag-manager/event-helpers/search";
import { formGTMEventGA4 } from "src/helpers/google-tag-manager/common";

import Dropdown from "components/common/dropdown";
import CheckBoxDropdown from "components/common/checkbox-dropdown";
import RangeSelectDropdown from "components/common/range-select-dropdown";
import Input from "components/common/input";
import Button from "components/common/button";

import styles from "./styles.module.css";

const SuggestionBox = dynamic(() => import("components/common/suggestion-box"), { ssr: false });
const MemoSuggestionBox = memo(SuggestionBox);

const ProjectsPanel = ({ projectCategories, pageType }) => {
  const router = useRouter();
  const isNotFoundPage = pageType !== "not-found";
  const categories =
    projectCategories?.child?.map((item) => ({
      key: item?.id,
      value: item?.name,
      ...item,
    })) || [];

  const [selected, setSelected] = useState(categories[0]);
  const [roomCountList, setRoomCountList] = useState([]);
  const [roomCountValues, setRoomCountValues] = useState([]);
  const [priceList, setPriceList] = useState([]);
  const [priceValues, setPriceValues] = useState([]);
  const [freeText, setFreeText] = useState("");
  const [poies, setPoies] = useState([]);
  const [locationKey, setLocationkey] = useState("");
  const [inputPlaceholder, setInputPlaceholder] = useState("İl, ilçe, mahalle, site, okul, metro…");

  const locationWrapperClass = classNames({
    [styles.locationinput]: true,
    [styles.locationinputWidth]: isNotFoundPage,
    [styles.landForFlat]: selected?.value !== "Konut" && isNotFoundPage,
  });

  const poiButtonClass = classNames({
    [styles.poiButtonText]: true,
    [styles.poiButtonTextNotFound]: !isNotFoundPage,
  });

  const closeIconClass = classNames({
    [styles.icon]: true,
    [styles.iconNotFound]: !isNotFoundPage,
  });

  const searchButtonClass = classNames({
    [styles.searchbutton]: true,
    [styles.searchbuttontop]: pageType === "not-found",
  });

  const handleChange = useCallback((selected) => {
    setSelected(selected);
  }, []);

  const fillFilterState = (data, key, setstate) => {
    const values = data?.result?.find((item) => item.key === key);
    const newList = values?.config?.values?.map((item) => ({
      key: item?.id,
      value: item?.name,
    }));

    setstate(newList);
  };

  const getFilters = async (categoryId, tradeTypeId) => {
    const data = await getPropertyTree({
      categoryId,
      tradeTypeId,
    });

    fillFilterState(data, "room_count", setRoomCountList);

    const price = data?.result?.find((item) => item.key === "price");
    const newPriceList = price?.config?.values?.map((item) => {
      const splitPrice = item.name.split(" TL");
      return { key: item?.id, value: splitPrice[0] };
    });

    setPriceList(newPriceList);
  };

  useEffect(() => {
    getFilters(selected?.key, selected?.tradeTypeId);
    if (selected?.value !== "Konut") {
      setRoomCountValues([]);
    }
  }, [selected]);

  const handleChangeText = (e) => {
    setFreeText(e.target.value);
  };

  const handlePOIClick = useCallback((item, key) => {
    if (key === "projects") {
      window.location = item?.url;
    }
    const location = item;
    setInputPlaceholder("");
    setFreeText("");
    setPoies([location]);
    setLocationkey(key);
  }, []);

  const handlePoiCloseButtonClick = useCallback(() => {
    setInputPlaceholder("İl, ilçe, mahalle, site, okul, metro…");
    setPoies([]);
  }, []);

  const getCriteria = () => {
    return {
      category: {
        key: "category",
        category: {
          id: selected?.key,
          slug: selected?.categorySlug,
        },
        tradeType: {
          id: selected?.tradeTypeId,
          slug: selected?.tradeTypeSlug,
        },
      },
      location:
        poies.length > 0
          ? {
              city: poies[0]?.city?.id
                ? {
                    id: poies[0]?.city.id,
                    name: poies[0]?.city.name,
                    slug: poies[0]?.city.slug,
                  }
                : {},
              districts: poies[0]?.district?.id
                ? [
                    {
                      id: poies[0]?.district.id,
                      name: poies[0]?.district.name,
                      slug: poies[0]?.district.slug,
                    },
                  ]
                : [],
              towns:
                poies[0].type === "town"
                  ? [
                      {
                        id: poies[0]?.id,
                        name: poies[0]?.name,
                        slug: poies[0]?.slug,
                      },
                    ]
                  : [],
              sites:
                locationKey === "site"
                  ? [
                      {
                        id: poies[0]?.id,
                        name: poies[0]?.name,
                        displayName: poies[0]?.displayName,
                        url: poies[0]?.url,
                        cityId: poies[0]?.cityId,
                      },
                    ]
                  : [],
              localities:
                poies[0]?.type === "locality"
                  ? [
                      {
                        id: poies[0]?.id,
                        slug: poies[0]?.slug,
                      },
                    ]
                  : [],
              poiSet:
                locationKey === "poi"
                  ? [
                      {
                        id: poies[0]?.id,
                        name: poies[0]?.name,
                        url: poies[0]?.url,
                        slug: poies[0]?.slug,
                        type: "poies",
                      },
                    ]
                  : [],
              singleDistrict: true,
            }
          : null,
      params: [
        {
          key: "price",
          value: {
            min: priceValues?.min || null,
            max: priceValues?.max || null,
            ...(priceValues.min || priceValues.max ? { currency: "try" } : {}),
          },
        },
        {
          key: "room_count",
          value: roomCountValues || null,
        },
        {
          key: "owner",
          value: {
            id: "projeler",
            name: "Proje Firmasından",
          },
          ownerType: "PROJECTS",
        },
      ],
      freetext: freeText
        ? {
            key: "free_text",
            value: freeText,
          }
        : null,
    };
  };

  const handleSearchButton = useCallback(
    async (isRotatedToMapPage) => {
      const isNumberOfFreeText = !isNaN(freeText);

      const criteria = getCriteria();
      const res = await urlMaker(criteria, "default");

      const ga4SearchEventParams = getGa4SearchEventParams({
        btnCategory: isRotatedToMapPage ? "haritada-ara" : "search",
        criteriaState: criteria,
        activeTab: "projeler",
        poies,
      });
      runIdle(() => {
        formGTMEventGA4({ eventName: "search", eventParameters: ga4SearchEventParams });
      }, 1000);

      if (freeText && isNumberOfFreeText) {
        const criteriaPath = res?.path;
        const splitedUrl = criteriaPath.split("?filtreler=q=")[1];
        startTransition(() => {
          router.push(`/ilan/${splitedUrl}`);
        });
        return;
      }

      if (!isRotatedToMapPage) {
        startTransition(() => {
          router.push(res?.path);
        });
      } else {
        const url = res?.path?.includes("?") ? `${res?.path}&view_type=map` : `${res?.path}?view_type=map`;
        startTransition(() => {
          router.push(url);
        });
      }
    },
    [freeText, poies, selected, priceValues, roomCountValues, router]
  );

  const handleSearchClick = useCallback(() => handleSearchButton(false), [handleSearchButton]);
  const handleMapSearchClick = useCallback(() => handleSearchButton(true), [handleSearchButton]);

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === "Enter") {
        event.preventDefault();
        handleSearchClick();
        return;
      }
      if (freeText?.length === 0) {
        if (event.key === "Backspace") {
          setInputPlaceholder("İl, ilçe, mahalle, site, okul, metro…");
          setPoies([]);
        }
      }
    },
    [freeText, handleSearchClick]
  );

  const handleChangeRoomCountCheckbox = useCallback((item) => {
    const newItem = item.map(({ key, value, ...rest }) => ({ id: key, name: value, ...rest }));
    startTransition(() => {
      setRoomCountValues(newItem);
    });
  }, []);

  const handlePriceRange = useCallback((min = null, max = null) => {
    const numberMin = isNaN(min) ? Number(min?.value?.split(".").join("")) : min;
    const numberMax = isNaN(max) ? Number(max?.value?.split(".").join("")) : max;
    startTransition(() => {
      setPriceValues({ min: numberMin, max: numberMax });
    });
  }, []);

  return (
    <div className={styles.tabpanel}>
      <div className={styles.field}>
        {isNotFoundPage && <span className={styles.title}>Proje Tipi</span>}
        <div className={styles.typeinput}>
          <Dropdown options={categories} onChange={handleChange} listClass={styles.list} />
        </div>
      </div>
      <div className={`${styles.wfull} ${styles.field}`}>
        {isNotFoundPage && <span className={styles.title}>Konum Bilgisi</span>}
        <div className={locationWrapperClass}>
          {poies?.map((poi, index) => (
            <div key={index} className={styles.buttonWrapper}>
              <Button tooltip={poi?.name} flow="down" size="Auto" buttonClass={styles.buttonClass}>
                <span className={poiButtonClass}>{poi?.name}</span>
                <CloseButton onClick={() => handlePoiCloseButtonClick()} className={closeIconClass} />
              </Button>
            </div>
          ))}
          <div className={styles.inputWrapper}>
            <Input
              placeholder={inputPlaceholder}
              onChange={handleChangeText}
              value={freeText}
              inputClass={styles.inputClass}
              onKeyDown={handleKeyDown}
            />
          </div>
        </div>
        <MemoSuggestionBox
          freeText={freeText}
          categoryId={selected?.key}
          tradeTypeId={selected?.tradeTypeId}
          handlePOIClick={handlePOIClick}
          isProject={true}
        />
      </div>
      {selected?.value === "Konut" && isNotFoundPage && (
        <div className={styles.field}>
          <span className={styles.title}>Oda Sayısı</span>
          <div className={styles.roominput}>
            <CheckBoxDropdown
              onChange={handleChangeRoomCountCheckbox}
              placeHolder="Oda Giriniz"
              listClass={styles.checkboxwidth}
              options={roomCountList}
            />
          </div>
        </div>
      )}
      {isNotFoundPage && (
        <div className={styles.field}>
          <span className={styles.title}>Fiyat Bilgisi</span>
          <div className={styles.priceinput}>
            <RangeSelectDropdown
              placeHolder="Fiyat Giriniz"
              onChange={handlePriceRange}
              listClass={styles.rangeWidth}
              minList={priceList}
              maxList={priceList}
              type="TL"
            />
          </div>
        </div>
      )}
      <div className={`${styles.field} h-full flex items-end`}>
        <div className={searchButtonClass}>
          <Button onClick={handleSearchClick} size="Auto">
            Ara
          </Button>
        </div>
      </div>
      {isNotFoundPage && (
        <div className="h-full flex items-end">
          <div className={searchButtonClass}>
            <Button onClick={handleMapSearchClick} size="Auto" outline buttonClass={styles.mapButtonClass}>
              Haritada Ara
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

ProjectsPanel.propTypes = {
  projectCategories: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  pageType: PropTypes.string,
};

export default ProjectsPanel;
